/**
 * Átadás és lezárás — the Handover and close-out view of handover rev7 §11.7,
 * and the two transitions of §11.2:
 *
 *   frozen    → delivered   handover recorded: time, recipient, RevoBox out
 *   delivered → closed      leftovers where observed; RevoBox back or written off
 *
 * §11.10 says what is kept: the suggested and ordered portions per line, which
 * the order already carries, leftovers **where staff actually see them**, and
 * the RevoBox count out and back.
 *
 * Two things the screen is careful about:
 *
 *  - **Null is not zero.** §11.10: "null means not observed, which is normal
 *    for drop-off". An empty leftovers box is left empty and is sent as
 *    nothing; it never becomes a 0 that reads as "all eaten".
 *  - **No price.** This is an operations screen and the figures on it are
 *    portions and boxes. The money is on the order detail.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'

import { asDate, asMoment, fmt, Loading, statusText, useConsoleAction, useConsoleData } from '../common.tsx'

interface Line {
  line_id: string
  dish_id: string
  name_hu: string | null
  is_gf_portion: boolean
  is_drink: boolean
  suggested_portions: number
  ordered_portions: number
  leftover_portions: number | null
}

interface HandoverOrder {
  id: string
  ref: string
  status: string
  status_reason: string | null
  event_date: string
  event_start: string | null
  guests: number
  gf_guests: number
  customer_name: string
  customer_phone: string | null
  fulfilment: string
  slot_label: string | null
  delivered_at: string | null
  closed_at: string | null
  handover_recipient: string | null
  revobox_out: number | null
  revobox_returned: number | null
  revobox_written_off: number | null
  can_record_handover: boolean
  can_close: boolean
  leftovers_observed: boolean
  lines: Line[]
}

/** A text box that means "not answered" when it is empty (§11.10). */
const asOptionalNumber = (value: string): number | null =>
  value.trim() === '' ? null : Number(value)

export function Handover(): JSX.Element {
  const { data, error, loading, reload } = useConsoleData<{
    orders: HandoverOrder[]
    from: string
    to: string
    today: string
  }>('handover_queue', {})
  const { run, busy, error: actionError } = useConsoleAction()

  const [recipient, setRecipient] = useState<Record<string, string>>({})
  const [boxesOut, setBoxesOut] = useState<Record<string, string>>({})
  const [boxesBack, setBoxesBack] = useState<Record<string, string>>({})
  const [leftovers, setLeftovers] = useState<Record<string, string>>({})

  async function recordHandover(order: HandoverOrder): Promise<void> {
    const out = asOptionalNumber(boxesOut[order.id] ?? '')
    if (
      await run('handover_record', {
        order_id: order.id,
        recipient: recipient[order.id] ?? '',
        revobox_out: out,
      })
    ) {
      reload()
    }
  }

  async function recordCloseout(order: HandoverOrder, close: boolean): Promise<void> {
    const entries = order.lines
      .filter((line) => leftovers[line.line_id] !== undefined)
      .map((line) => ({
        line_id: line.line_id,
        // An empty box is "not observed" and is sent as such, never as 0.
        leftover_portions: leftovers[line.line_id]?.trim() === ''
          ? null
          : Number(leftovers[line.line_id]),
      }))
    if (
      await run('closeout_record', {
        order_id: order.id,
        leftovers: entries.length > 0 ? entries : null,
        revobox_returned: asOptionalNumber(boxesBack[order.id] ?? ''),
        close,
      })
    ) {
      reload()
    }
  }

  return (
    <section data-testid="view-handover">
      <h2>Átadás és lezárás</h2>
      <p className="lead">
        §11.2 és §11.10: az átadás ideje, átvevője és a kiadott RevoBox dobozok száma; utána a
        maradék — ott, ahol a kollégánk ténylegesen látta — és a visszaérkezett dobozok. Ami nem
        volt megfigyelhető, az üresen marad: az üres mező nem nulla.
      </p>

      <Loading loading={loading} error={error} />
      {actionError !== null && <p className="note error" data-testid="action-error">{actionError}</p>}

      {data !== null && data.orders.length === 0 && (
        <p className="note" data-testid="handover-empty">
          Nincs átadásra vagy lezárásra váró rendelés {asDate(data.from)} és {asDate(data.to)} között.
        </p>
      )}

      {data?.orders.map((order) => (
        <article className="card" key={order.id} data-testid={`handover-${order.ref}`}>
          <h3>
            <Link to={`/staff/orders/${order.id}`}>{order.ref}</Link>
            {' · '}
            {asDate(order.event_date, true)}
            {order.slot_label !== null && ` · ${order.slot_label}`}
            {' · '}
            {fmt.n(order.guests)} fő
            {' · '}
            {statusText(order.status, order.status_reason)}
          </h3>

          <p className="note">
            {order.fulfilment === 'pickup' ? 'Személyes átvétel' : 'Kiszállítás'}
            {' · '}
            {order.customer_name}
            {order.customer_phone !== null && ` · ${order.customer_phone}`}
          </p>

          {order.delivered_at !== null && (
            <p className="note" data-testid={`delivered-${order.ref}`}>
              Átadva: {asMoment(order.delivered_at)}
              {order.handover_recipient !== null && ` · átvette: ${order.handover_recipient}`}
              {order.revobox_out !== null && ` · ${fmt.n(order.revobox_out)} RevoBox kiadva`}
              {order.revobox_returned !== null &&
                ` · ${fmt.n(order.revobox_returned)} visszaérkezett`}
              {order.revobox_written_off !== null && order.revobox_written_off > 0 &&
                ` · ${fmt.n(order.revobox_written_off)} leírva`}
            </p>
          )}

          {order.can_record_handover && (
            <>
              <label htmlFor={`rec-${order.id}`}>Átvette (kötelező, §11.2)</label>
              <input
                id={`rec-${order.id}`}
                type="text"
                value={recipient[order.id] ?? ''}
                onChange={(event) =>
                  setRecipient((previous) => ({ ...previous, [order.id]: event.target.value }))}
                data-testid={`recipient-${order.ref}`}
              />
              <label htmlFor={`out-${order.id}`}>Kiadott RevoBox dobozok</label>
              <input
                id={`out-${order.id}`}
                type="number"
                min={0}
                value={boxesOut[order.id] ?? ''}
                onChange={(event) =>
                  setBoxesOut((previous) => ({ ...previous, [order.id]: event.target.value }))}
                data-testid={`revobox-out-${order.ref}`}
              />
              <div className="actions">
                <button
                  type="button"
                  className="primary"
                  disabled={busy || (recipient[order.id] ?? '').trim() === ''}
                  onClick={() => void recordHandover(order)}
                  data-testid={`record-handover-${order.ref}`}
                >
                  Átadás rögzítése
                </button>
              </div>
            </>
          )}

          {(order.can_close || order.status === 'closed') && (
            <>
              <table>
                <thead>
                  <tr>
                    <th>Fogás</th>
                    <th className="num">Javasolt</th>
                    <th className="num">Rendelt</th>
                    <th className="num">Maradék</th>
                  </tr>
                </thead>
                <tbody>
                  {order.lines.map((line) => (
                    <tr key={line.line_id}>
                      <td>
                        {line.name_hu ?? line.dish_id}
                        {line.is_gf_portion && <span className="tag ok">gluténmentes</span>}
                      </td>
                      <td className="num">{fmt.n(line.suggested_portions)}</td>
                      <td className="num">{fmt.n(line.ordered_portions)}</td>
                      <td className="num">
                        {order.status === 'closed'
                          ? (line.leftover_portions === null
                            ? 'nem volt megfigyelhető'
                            : fmt.n(line.leftover_portions))
                          : (
                            <input
                              type="number"
                              min={0}
                              max={line.ordered_portions}
                              value={leftovers[line.line_id]
                                ?? (line.leftover_portions === null
                                  ? ''
                                  : String(line.leftover_portions))}
                              onChange={(event) =>
                                setLeftovers((previous) => ({
                                  ...previous,
                                  [line.line_id]: event.target.value,
                                }))}
                              data-testid={`leftover-${order.ref}-${line.dish_id}${
                                line.is_gf_portion ? '-gf' : ''
                              }`}
                            />
                          )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {order.status !== 'closed' && (
                <>
                  <p className="note">
                    §11.10: a maradékot csak ott rögzítjük, ahol tényleg láttuk. Üres mező =
                    nem volt megfigyelhető, és ez kiszállításnál a szokásos.
                  </p>
                  <label htmlFor={`back-${order.id}`}>
                    Visszaérkezett RevoBox dobozok
                    {order.revobox_out !== null && ` (kiadva: ${fmt.n(order.revobox_out)})`}
                  </label>
                  <input
                    id={`back-${order.id}`}
                    type="number"
                    min={0}
                    max={order.revobox_out ?? undefined}
                    value={boxesBack[order.id]
                      ?? (order.revobox_returned === null ? '' : String(order.revobox_returned))}
                    onChange={(event) =>
                      setBoxesBack((previous) => ({ ...previous, [order.id]: event.target.value }))}
                    data-testid={`revobox-back-${order.ref}`}
                  />
                  <div className="actions">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void recordCloseout(order, false)}
                      data-testid={`save-closeout-${order.ref}`}
                    >
                      Mentés lezárás nélkül
                    </button>
                    <button
                      type="button"
                      className="primary"
                      disabled={busy}
                      onClick={() => void recordCloseout(order, true)}
                      data-testid={`close-${order.ref}`}
                    >
                      Lezárás
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </article>
      ))}
    </section>
  )
}
